import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Music, Loader2, CheckCircle, AlertCircle, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const MUSIC_VARIATION_ID = 'U34JWMSAM6CNSAYQD3ZDIAWU';

const RequestSongPage = () => {
  const [step, setStep] = useState('loading');
  const [songTitle, setSongTitle] = useState('');
  const [artistName, setArtistName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const pollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if music requests are enabled
    const checkAvailability = async () => {
      try {
        const r = await fetch(`${API}/api/site-settings`);
        if (r.ok) {
          const settings = await r.json();
          if (settings.music_requests_enabled === false) {
            setStep('unavailable');
            return;
          }
        }
      } catch (e) { /* assume enabled on error */ }

      // Check for checkout return
      const params = new URLSearchParams(window.location.search);
      if (params.get('checkout') === 'complete') {
        window.history.replaceState({}, '', window.location.pathname);
        const pendingId = sessionStorage.getItem('med_pending_music_id');
        const orderId = sessionStorage.getItem('med_pending_music_order');
        if (pendingId && orderId) {
          setStep('confirming');
          startPolling(pendingId, orderId);
          return;
        }
      }
      setStep('form');
    };
    checkAvailability();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const startPolling = (pendingId, orderId) => {
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(`${API}/api/music-requests/pending/${pendingId}/status?order_id=${orderId}`);
        const data = await res.json();
        if (data.status && data.status !== 'pending_payment') {
          clearInterval(pollRef.current);
          sessionStorage.removeItem('med_pending_music_id');
          sessionStorage.removeItem('med_pending_music_order');
          if (data.status === 'expired') {
            setError('Request expired. Please try again.');
            setStep('error');
          } else {
            setSongTitle(data.song_title || '');
            setArtistName(data.artist_name || '');
            setStep('submitted');
          }
          return;
        }
      } catch (e) { /* retry */ }
      if (attempts >= 60) {
        clearInterval(pollRef.current);
        setError('Payment verification timed out. If you were charged, your request will be processed automatically.');
        setStep('error');
      }
    }, 2000);
  };

  const handlePayAndRequest = async () => {
    const song = songTitle.trim();
    const artist = artistName.trim();
    if (!song || !artist) {
      setError('Song title and artist are required.');
      return;
    }
    setError('');
    setStep('processing');
    try {
      const pendingRes = await fetch(`${API}/api/music-requests/pending`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          song_title: song,
          artist_name: artist,
          display_name: displayName.trim() || null,
          message: message.trim() || null,
        }),
      });
      if (!pendingRes.ok) {
        const err = await pendingRes.json();
        throw new Error(err.detail || 'Failed to save request');
      }
      const { pending_id } = await pendingRes.json();

      const checkoutRes = await fetch(`${API}/api/store/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variation_id: MUSIC_VARIATION_ID,
          quantity: 1,
          entitlement_type: 'music_request',
          redirect_path: '/request-song',
          pending_submission_id: pending_id,
        }),
      });
      if (!checkoutRes.ok) throw new Error('Failed to create checkout');
      const { url, order_id } = await checkoutRes.json();

      sessionStorage.setItem('med_pending_music_id', pending_id);
      sessionStorage.setItem('med_pending_music_order', order_id);
      window.location.href = url;
    } catch (err) {
      setError(err.message);
      setStep('form');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0a2e] to-[#0d1b2a] flex items-center justify-center p-4" data-testid="request-song-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-600/20 border border-amber-500/30 mb-4">
            <Music className="w-8 h-8 text-amber-400" />
          </div>
          <h1 className="text-2xl font-bold text-white">Request a Song</h1>
          <p className="text-white/60 text-sm mt-1">$8 per request</p>
        </div>

        {step === 'loading' && (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 text-amber-400 animate-spin mx-auto" />
          </div>
        )}

        {step === 'unavailable' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="bg-white/5 backdrop-blur-lg rounded-xl border border-white/10 p-8 text-center"
            data-testid="music-unavailable">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#E91E8D]/10 border border-[#E91E8D]/20 mb-5">
              <Music className="w-7 h-7 text-[#E91E8D]/60" />
            </div>
            <h2 className="text-white font-bold text-lg mb-2">Music Requests Are Currently Closed</h2>
            <p className="text-white/40 text-sm leading-relaxed mb-6">
              Music requests are not available at this time.<br />Please check back later.
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white/50 text-sm font-medium hover:bg-white/10 transition-colors"
              data-testid="back-home-btn">
              Back to Home
            </button>
          </motion.div>
        )}

        {step === 'form' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white/5 backdrop-blur-lg rounded-xl border border-white/10 p-6 space-y-4" data-testid="music-form">
            <div>
              <label className="block text-amber-200 text-sm mb-1">Song Title *</label>
              <input
                type="text" value={songTitle} onChange={e => setSongTitle(e.target.value)}
                placeholder="Enter song title" maxLength={200}
                className="w-full bg-black/20 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:border-amber-500 focus:outline-none"
                data-testid="input-song-title"
              />
            </div>
            <div>
              <label className="block text-amber-200 text-sm mb-1">Artist *</label>
              <input
                type="text" value={artistName} onChange={e => setArtistName(e.target.value)}
                placeholder="Enter artist name" maxLength={200}
                className="w-full bg-black/20 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:border-amber-500 focus:outline-none"
                data-testid="input-artist-name"
              />
            </div>
            <div>
              <label className="block text-amber-200 text-sm mb-1">Your Name (optional)</label>
              <input
                type="text" value={displayName} onChange={e => setDisplayName(e.target.value)}
                placeholder="Display name"
                className="w-full bg-black/20 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:border-amber-500 focus:outline-none"
                data-testid="input-display-name"
              />
            </div>
            <div>
              <label className="block text-amber-200 text-sm mb-1">Message (optional)</label>
              <textarea
                value={message} onChange={e => setMessage(e.target.value)}
                placeholder="Any special message..." rows={2}
                className="w-full bg-black/20 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:border-amber-500 focus:outline-none resize-none"
                data-testid="input-message"
              />
            </div>
            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 shrink-0" /> <span>{error}</span>
              </div>
            )}
            <button
              onClick={handlePayAndRequest}
              disabled={!songTitle.trim() || !artistName.trim()}
              className="w-full bg-gradient-to-r from-amber-600 to-amber-800 hover:from-amber-500 hover:to-amber-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-6 py-4 transition-all"
              data-testid="pay-and-request-btn"
            >
              Pay $8 & Request Song
            </button>
            <p className="text-white/30 text-xs text-center">Secure payment via Square. One payment = one song request.</p>
          </motion.div>
        )}

        {step === 'processing' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white/5 backdrop-blur-lg rounded-xl border border-white/10 p-8 text-center" data-testid="music-processing">
            <Loader2 className="w-10 h-10 text-amber-400 animate-spin mx-auto mb-4" />
            <p className="text-white font-medium">Preparing your request...</p>
            <p className="text-white/40 text-sm mt-1">Redirecting to payment</p>
          </motion.div>
        )}

        {step === 'confirming' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white/5 backdrop-blur-lg rounded-xl border border-white/10 p-8 text-center" data-testid="music-confirming">
            <Loader2 className="w-10 h-10 text-amber-400 animate-spin mx-auto mb-4" />
            <p className="text-white font-medium">Verifying payment...</p>
            <p className="text-white/40 text-sm mt-1">Your song will be submitted automatically</p>
          </motion.div>
        )}

        {step === 'submitted' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 backdrop-blur-lg rounded-xl border border-green-400/30 p-8 text-center"
            data-testid="music-submitted"
          >
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Song Requested!</h2>
            {songTitle && artistName && (
              <p className="text-white/60 text-sm">
                <span className="text-amber-300 font-medium">{songTitle}</span> by <span className="text-amber-300 font-medium">{artistName}</span>
              </p>
            )}
            <p className="text-white/40 text-xs mt-3">Your request is now live on the board.</p>
            <a href="/" className="inline-block mt-6 text-amber-400 hover:text-amber-300 text-sm font-medium" data-testid="back-home-link">
              ← Back to Mediterranean Club
            </a>
          </motion.div>
        )}

        {step === 'error' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-red-500/10 backdrop-blur-lg rounded-xl border border-red-400/30 p-8 text-center" data-testid="music-error">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-white mb-2">Something went wrong</h2>
            <p className="text-white/60 text-sm">{error}</p>
            <button onClick={() => { setStep('form'); setError(''); }} className="mt-4 text-amber-400 hover:text-amber-300 text-sm font-medium">
              Try again
            </button>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default RequestSongPage;
